import { useState, useCallback } from 'react';
import { ShotVersion, ShotVersionType, ShotVersionService } from '@/services/supabase/shotVersions';
import type { PortfolioItem } from '@/hooks/profile';

interface UseShotVersionHistoryOptions {
  shotId?: string;
  profileId?: string;
  autoLoad?: boolean;
}

interface UseShotVersionHistoryReturn {
  versions: ShotVersion[];
  isLoading: boolean;
  isSaving: boolean;
  error: Error | null;
  loadVersions: () => Promise<void>;
  createVersion: (shot: PortfolioItem, label?: string, versionType?: ShotVersionType) => Promise<ShotVersion>;
  deleteVersion: (versionId: string) => Promise<void>;
  getVersion: (versionId: string) => Promise<ShotVersion>;
  refreshVersions: () => Promise<void>;
}

/**
 * Hook for managing shot version history
 * Handles loading, creating, and deleting versions
 */
export function useShotVersionHistory(
  options: UseShotVersionHistoryOptions = {}
): UseShotVersionHistoryReturn {
  const { shotId, profileId, autoLoad = false } = options;

  const [versions, setVersions] = useState<ShotVersion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Load all versions for the shot
   */
  const loadVersions = useCallback(async () => {
    if (!shotId) {
      setVersions([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const loadedVersions = await ShotVersionService.getVersions(shotId);
      setVersions(loadedVersions);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load versions');
      setError(error);
      console.error('[useShotVersionHistory] Error loading versions:', err);
    } finally {
      setIsLoading(false);
    }
  }, [shotId]);

  /**
   * Create a new version
   */
  const createVersion = useCallback(
    async (
      shot: PortfolioItem,
      label?: string,
      versionType: ShotVersionType = 'manual'
    ): Promise<ShotVersion> => {
      if (!profileId) {
        throw new Error('Profile ID is required to create a version');
      }

      setIsSaving(true);
      setError(null);

      try {
        const newVersion = await ShotVersionService.createVersion(
          shot,
          profileId,
          label,
          versionType
        );

        // Add to local state (prepend since we order by version_number DESC)
        setVersions((prev) => [newVersion, ...prev]);

        return newVersion;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to create version');
        setError(error);
        console.error('[useShotVersionHistory] Error creating version:', err);
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [profileId]
  );

  /**
   * Delete a version
   */
  const deleteVersion = useCallback(async (versionId: string): Promise<void> => {
    setError(null);

    try {
      await ShotVersionService.deleteVersion(versionId);

      // Remove from local state
      setVersions((prev) => prev.filter((v) => v.id !== versionId));
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to delete version');
      setError(error);
      console.error('[useShotVersionHistory] Error deleting version:', err);
      throw error;
    }
  }, []);

  /**
   * Get a specific version by ID
   */
  const getVersion = useCallback(async (versionId: string): Promise<ShotVersion> => {
    setError(null);

    try {
      return await ShotVersionService.getVersion(versionId);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to get version');
      setError(error);
      console.error('[useShotVersionHistory] Error getting version:', err);
      throw error;
    }
  }, []);

  /**
   * Refresh versions list
   */
  const refreshVersions = useCallback(async () => {
    await loadVersions();
  }, [loadVersions]);

  return {
    versions,
    isLoading,
    isSaving,
    error,
    loadVersions,
    createVersion,
    deleteVersion,
    getVersion,
    refreshVersions,
  };
}
